import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  AddCircleOutline as AddCircleOutlineIcon,
  Close as CloseIcon,
  Pause as PauseIcon,
  VolumeUp as VolumeUpIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { _delete, _file } from "../services/api.service";
import { _notify, NotificationType } from "../services/notification.service";

const MAX_FILE_SIZE = 2 * 1024 * 1024;

export interface DropdownOption {
  id: number;
  label: string;
  actions: string[];
  audio_url?: string;
  url?: string;
  isClicked: boolean;
  is_shared?: boolean;
  voice_id?: number;
}

interface SelectAudioDropdownProps {
  options: DropdownOption[];
  label: string;
  voiceId: number;
  shouldFirstSelected: boolean;
  showAddMusic?: boolean;
  showClearSelection: boolean;
  onAction: (
    option: DropdownOption | null,
    action: string,
    isAlreadySelected: boolean
  ) => void;
  setOptions: (opts: DropdownOption[]) => void;
}

const SelectAudioDropdown: React.FC<SelectAudioDropdownProps> = ({
  label,
  options,
  voiceId,
  shouldFirstSelected,
  showAddMusic = false,
  showClearSelection = true,
  onAction,
  setOptions,
}) => {
  const [selectedOption, setSelectedOption] = useState<DropdownOption | null>(
    null
  );
  const [listeningLabel, setListeningLabel] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (options.length && shouldFirstSelected) {
      const selected = options.find((o) => o.id === voiceId) || options[0];
      setSelectedOption(selected);
    }
  }, [options, voiceId, shouldFirstSelected]);

  useEffect(() => () => stopAudio(), []);

  const stopAudio = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      setListeningLabel(null);
    }
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    stopAudio();
  };

  const handleSelect = (option: DropdownOption) => {
    stopAudio();
    setSelectedOption(option);
    onAction(option, "select", false);
    setAnchorEl(null);
  };

  const handleAction = useCallback(
    (action: string, option: DropdownOption) => {
      const isListening = listeningLabel === option.label;
      if (action === "listen") {
        if (isListening) return stopAudio();
        stopAudio();
        const url = option.audio_url ?? option.url;
        if (url) {
          const audio = new Audio(url);
          audioRef.current = audio;
          audio
            .play()
            .catch(() =>
              _notify(NotificationType.ERROR, "Unable to play audio")
            );
          setListeningLabel(option.label);
          audio.onended = () => setListeningLabel(null);
        }
      }
    },
    [listeningLabel, stopAudio]
  );

  const clearSelected = useCallback(() => {
    stopAudio();
    setSelectedOption(null);
    onAction(null, "select", false);
  }, [onAction, stopAudio]);

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;
      if (file.size > MAX_FILE_SIZE) {
        _notify(
          NotificationType.ERROR,
          `File size exceeds 2 MB limit (${(file.size / 1024).toFixed(1)} KB).`
        );
        return;
      }
      if (options.some((o) => o.label === file.name)) {
        _notify(NotificationType.ERROR, "Audio with same name already exists");
        return;
      }
      try {
        setUploading(true);
        const uploadResponse = await _file("engine/file-upload/", {
          fileType: 4,
          file,
        }, {}, true);
        if (uploadResponse?.id && uploadResponse?.url) {
          const newAudio: DropdownOption = {
            id: uploadResponse.id,
            label: file.name,
            actions: ["listen"],
            audio_url: uploadResponse.url,
            isClicked: false,
            is_shared: false,
          };
          const updatedOptions = [...options, newAudio];
          setOptions(updatedOptions);
          setSelectedOption(newAudio);
          onAction(newAudio, "select", false);
          _notify(NotificationType.SUCCESS, "Audio uploaded successfully");
        }
      } catch {
        _notify(NotificationType.ERROR, "Failed to upload audio");
      } finally {
        setUploading(false);
      }
    },
    [options, setOptions, onAction]
  );

  const handleDeleteAudio = useCallback(
    async (id: number) => {
      try {
        const response = await _delete(`engine/file-upload/${id}/`);
        if (response) {
          setOptions(options.filter((o) => o.id !== id));
          _notify(NotificationType.SUCCESS, "Audio deleted successfully");
        }
      } catch {
        _notify(
          NotificationType.ERROR,
          "An error occurred while deleting audio"
        );
      }
    },
    [options, setOptions]
  );

  return (
    <Box>
      <Box sx={{ position: "relative" }}>
        <Button
          onClick={handleClick}
          fullWidth
          endIcon={<KeyboardArrowDownIcon />}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "grey.200",
            bgcolor: "white",
            color: "text.primary",
            textTransform: "none",
            fontSize: 14,
            fontWeight: 500,
            "&:hover": {
              borderColor: "grey.400",
              bgcolor: "grey.50",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <VolumeUpIcon sx={{ fontSize: 20, color: "text.secondary" }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {selectedOption?.label || label}
            </Typography>
          </Box>
        </Button>

        {selectedOption && showClearSelection && (
          <IconButton
            size="small"
            onClick={clearSelected}
            sx={{
              position: "absolute",
              right: 40,
              top: "50%",
              transform: "translateY(-50%)",
              color: "text.disabled",
              "&:hover": { color: "text.primary" },
            }}
            title="Clear selection"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              width: anchorEl?.offsetWidth,
              borderRadius: 2,
              boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
              "& .MuiList-root": { py: 0 },
            },
          },
        }}
      >
        {options.map((option) => {
          const isListening = listeningLabel === option.label;
          return (
            <MenuItem
              key={option.id}
              selected={option.id === voiceId}
              onClick={() => handleSelect(option)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                px: 2,
                py: 1.5,
                borderBottom: "1px solid",
                borderColor: "grey.100",
                "&:last-child": { borderBottom: "none" },
                "&.Mui-selected": {
                  bgcolor: "rgba(94, 106, 210, 0.08)",
                  "&:hover": { bgcolor: "rgba(94, 106, 210, 0.12)" },
                },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {option.label}
                </Typography>
              </Box>
              <Box
                onClick={(e) => e.stopPropagation()}
                sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
              >
                <Box
                  onClick={() => handleAction("listen", option)}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: isListening ? "primary.main" : "text.secondary",
                    bgcolor: isListening ? "rgba(94, 106, 210, 0.1)" : "transparent",
                    transition: "all 0.15s",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  {isListening ? <PauseIcon sx={{ fontSize: 18 }} /> : <VolumeUpIcon sx={{ fontSize: 18 }} />}
                </Box>
                {option.is_shared === false && (
                  <Box
                    onClick={() => handleDeleteAudio(option.id)}
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      color: "text.disabled",
                      transition: "all 0.15s",
                      "&:hover": { color: "error.main", bgcolor: "rgba(211, 47, 47, 0.06)" },
                    }}
                  >
                    <FontAwesomeIcon icon={faTrashCan} style={{ fontSize: 12 }} />
                  </Box>
                )}
              </Box>
            </MenuItem>
          );
        })}
        {showAddMusic && (
          <Box
            onClick={() => fileInputRef.current?.click()}
            sx={{
              px: 2,
              py: 1.5,
              borderTop: "1px solid",
              borderColor: "grey.100",
              cursor: "pointer",
              transition: "background 0.15s",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {uploading ? (
                <CircularProgress size={18} />
              ) : (
                <AddCircleOutlineIcon color="primary" />
              )}
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {uploading ? "Uploading..." : "Add Music"}
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: "text.disabled", ml: 4, mt: 0.3, display: "block" }}>
              .mp3 file smaller than 2MB
            </Typography>
          </Box>
        )}
      </Menu>
      <input
        type="file"
        accept="audio/*"
        ref={fileInputRef}
        onChange={handleFileSelect}
        style={{ display: "none" }}
      />
    </Box>
  );
};

export default SelectAudioDropdown;
